// @flow
import React, { useMemo, useState, useCallback } from 'react';
import { map } from 'lodash';
import type { ActionItems } from '@types';
import classNames from 'classnames';
import { ClickableList, Modal, SearchInput } from '@elements';
import { filterSearch, useTranslationNs } from '@helpers/helpers';
import { PlatformSwitch } from '@blocks';
import SheetGrabber from '../SheetGrabber/SheetGrabber';
import './ModalClickableList.scss';

type Props = {
  items: ActionItems,
  onListItemClick: Function,
  onClose: Function,
  notUsePortal?: boolean,
  search?: boolean,
  customRenderer?: Function, // (item) => (node(item)) -> return custom renderer element / node with corresponding data
  sheetCancel?: boolean, // false when the list has its own Cancelar row (the phone sheet then adds none)
};

// A title row ("Opções", "Meio de Pagamento") is a label, not an option: tapping it does nothing
const isHeading = (item: Object) => /\bheadingSeparator\b/.test(item.className || '');
const asLabel = (item: Object) => (isHeading(item) ? { ...item, preventClick: true } : item);

const ModalClickableList = ({ items, onClose, onListItemClick, notUsePortal, search, customRenderer, sheetCancel = true }: Props) => {
  const [_] = useTranslationNs('global');
  const [searchText, setSearchText] = useState('');

  const fItems = useMemo(() => map(filterSearch(items, searchText), asLabel), [items, searchText]);

  const handleChangeSearchInput = useCallback((value: string) => {
    setSearchText(value);
  }, []);

  const render = (isMobile: boolean) => (
    <Modal className={classNames('modalClickableList', 'clickableList', { searchable: search, isMobile })} onClose={onClose} throughPortal={!notUsePortal}>
      {/* On the phone the sheet can be pulled down to close, like tapping outside it */}
      {isMobile && <SheetGrabber onClose={onClose} />}
      {search && (
        <div className="searchInputBox">
          <SearchInput autofocus onChange={handleChangeSearchInput} uncontrolled className="searchInputList"/>
        </div>
      )}
      <ClickableList customRenderer={customRenderer} items={fItems} onListItemClick={onListItemClick} />
      {/* On the phone the list is a bottom sheet, closed by its own Cancelar instead of the corner X */}
      {isMobile && sheetCancel && <button type="button" className="sheetCancel" onClick={onClose}>{_('action/cancel')}</button>}
    </Modal>
  );

  return (
    <PlatformSwitch mobile={() => render(true)} desktop={() => render(false)}/>
  );
};

export default ModalClickableList;
